import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate, STATUS_COLORS, PRIORITY_COLORS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserCog, TriangleAlert, CheckCircle2, Activity } from "lucide-react";

function KPI({ label, value, icon: Icon, tone }) {
  return (
    <Card className="rounded-sm shadow-none border p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">{label}</div>
          <div className="font-heading font-black text-2xl mt-1">{value}</div>
        </div>
        <Icon className={`w-4 h-4 ${tone}`} />
      </div>
    </Card>
  );
}

export default function MyOS() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSupervisor = ["supervisor", "almoxarife"].includes(user?.role);
  const [orders, setOrders] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedTech, setSelectedTech] = useState(""); // supervisor selects; techs auto = self
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(() => api.get("/os").then((r) => setOrders(r.data)), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isSupervisor) {
      api.get("/users/technicians").then((r) => {
        setTechnicians(r.data);
        if (r.data.length > 0) setSelectedTech((prev) => prev || r.data[0].id);
      });
    } else if (user?.id) {
      setSelectedTech(user.id);
    }
  }, [isSupervisor, user?.id]);

  const activeTech = useMemo(
    () => technicians.find((t) => t.id === selectedTech) || (isSupervisor ? null : user),
    [technicians, selectedTech, isSupervisor, user],
  );

  const filtered = useMemo(() => {
    const techId = isSupervisor ? selectedTech : user?.id;
    return orders.filter((o) => {
      if (!techId) return false;
      if (o.technician_id !== techId && !(o.technician_ids || []).includes(techId)) return false;
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      return true;
    });
  }, [orders, selectedTech, statusFilter, isSupervisor, user?.id]);

  const [prevDone, setPrevDone] = useState([]);
  useEffect(() => {
    const techId = isSupervisor ? selectedTech : user?.id;
    if (!techId) { setPrevDone([]); return; }
    api.get("/preventive/completions", { params: { technician_id: techId } }).then((r) => setPrevDone(r.data)).catch(() => {});
  }, [isSupervisor, selectedTech, user?.id]);

  const stats = useMemo(() => ({
    total: filtered.length,
    open: filtered.filter((o) => o.status === "Aberta").length,
    in_progress: filtered.filter((o) => o.status === "Em Andamento").length,
    done: filtered.filter((o) => o.status === "Concluída").length,
  }), [filtered]);

  return (
    <div className="space-y-6" data-testid="my-os-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">
            {isSupervisor ? "Visão por Técnico" : "Minhas Ordens"}
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tight">
            {isSupervisor ? "OS por Técnico" : "Minhas OS"}
          </h1>
          {activeTech && (
            <p className="text-muted-foreground mt-1">
              {isSupervisor ? "Filtrando por " : "Atribuídas a "}
              <b>{activeTech.name}</b>
              {activeTech.email && <span className="text-xs ml-1">({activeTech.email})</span>}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {isSupervisor && (
            <Select value={selectedTech} onValueChange={setSelectedTech}>
              <SelectTrigger className="rounded-sm w-64" data-testid="tech-select">
                <SelectValue placeholder="Selecione um técnico" />
              </SelectTrigger>
              <SelectContent>
                {technicians.length === 0 ? (
                  <div className="p-2 text-xs text-muted-foreground">Nenhum técnico cadastrado</div>
                ) : technicians.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`tech-option-${t.id}`}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-sm w-48" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="Aberta">Aberta</SelectItem>
              <SelectItem value="Em Andamento">Em Andamento</SelectItem>
              <SelectItem value="Concluída">Concluída</SelectItem>
              <SelectItem value="Cancelada">Cancelada</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Total" value={stats.total} icon={UserCog} tone="text-orange-500" />
        <KPI label="Abertas" value={stats.open} icon={TriangleAlert} tone="text-red-500" />
        <KPI label="Em Andamento" value={stats.in_progress} icon={Activity} tone="text-yellow-500" />
        <KPI label="Concluídas" value={stats.done} icon={CheckCircle2} tone="text-green-500" />
      </div>

      {/* Table */}
      <Card className="rounded-sm shadow-none overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">OS</TableHead>
              <TableHead>Equipamento</TableHead>
              <TableHead className="hidden md:table-cell">Setor</TableHead>
              <TableHead className="hidden md:table-cell">Disciplina</TableHead>
              <TableHead>Prioridade</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden lg:table-cell">Aberto em</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isSupervisor && !selectedTech ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Selecione um técnico</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">
                {isSupervisor ? "Este técnico não tem OS no filtro selecionado" : "Você não tem OS atribuídas"}
              </TableCell></TableRow>
            ) : filtered.map((o) => (
              <TableRow
                key={o.id}
                onClick={() => navigate(`/os/${o.id}`)}
                className="cursor-pointer hover:bg-secondary"
                data-testid={`my-os-row-${o.number}`}
              >
                <TableCell className="font-mono-tag font-semibold">#{o.number}</TableCell>
                <TableCell>
                  <div className="font-medium">{o.equipment_name}</div>
                  <div className="text-xs font-mono-tag text-muted-foreground">{o.equipment_tag}</div>
                </TableCell>
                <TableCell className="hidden md:table-cell">{o.sector}</TableCell>
                <TableCell className="hidden md:table-cell">{o.discipline}</TableCell>
                <TableCell><Badge variant="outline" className={`rounded-sm ${PRIORITY_COLORS[o.priority]}`}>{o.priority}</Badge></TableCell>
                <TableCell><Badge variant="outline" className={`rounded-sm ${STATUS_COLORS[o.status]}`}>{o.status}</Badge></TableCell>
                <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{fmtDate(o.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="rounded-sm shadow-none overflow-hidden" data-testid="my-preventives">
        <div className="p-4 border-b font-heading font-bold">Preventivas executadas ({prevDone.length})</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead><TableHead>Equipamento</TableHead><TableHead>Tarefa</TableHead>
              <TableHead className="hidden md:table-cell">Equipe</TableHead><TableHead className="hidden lg:table-cell">Observações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {prevDone.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Nenhuma preventiva executada</TableCell></TableRow>
            ) : prevDone.map((c) => (
              <TableRow key={c.id} data-testid={`my-prev-row-${c.id}`}>
                <TableCell className="text-sm">{c.date}</TableCell>
                <TableCell><span className="font-mono-tag text-orange-600 mr-1">{c.equipment_tag}</span>{c.equipment_name}</TableCell>
                <TableCell>{c.task}</TableCell>
                <TableCell className="hidden md:table-cell text-sm">{c.technician_names?.join(", ")}</TableCell>
                <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{c.notes}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
